import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Button, DatePicker, Select, Space} from 'antd';
import dayjs from 'dayjs';
import {
  StyledTableFilters,
  StyledTableFiltersSearch,
  StyledTableFiltersElements,
  StyledTableFiltersElementsSpace,
  StyledTableFiltersSelect,
} from './StyledTableFilters';
import ExportModal from '../../TransactionsPage/ExportModal';
import {BudgetStatusesOptions, dateHelpers, fileHelpers, formHelpers, tableHelpers} from '../../../../utils/helpers';
import {StarsIcon} from '../../../../icons';
import {DEFAULT_MODAL_PROPS, agentTypeConstants} from '../../../../constants';
import {agentActions} from '../../../../state/actions/agent.actions';
import {budgetActions} from '../../../../state/actions/budget.actions';

const TableFilters = ({
  defaultQuery = {},
  enabledActionsSection = true,
  exportAdditionalFields = {},
  filterFields = ['search', 'status'],
  onFilter,
  tableType,

  ...rest
}) => {
  const [t] = useTranslation('main');
  const [filter, setFilter] = useState({
    active_approver: undefined,
    date_range: null,
    status: [],
    reported_by: undefined,
    search: undefined
  });
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);

  const [statusOptions, ] = useState(BudgetStatusesOptions(tableType));

  const dispatch = useDispatch();

  const {employees, employeeEmail} = useSelector(state => ({
    employees: state?.company?.employees || [],
    employeeEmail: state?.user?.employee?.email,
  }));

  const employeeOptions = useMemo(() =>
    formHelpers.getEmployeeOptions({employees, employeeEmail, t, valueKey: 'email'}), [employees, employeeEmail, t]);

  useEffect(() => {
    setFilter({...filter, ...defaultQuery});
  }, [defaultQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnFilter = (key, value) => {
    const { updatedValues, formattedValues } = tableHelpers.formatFilterPayload(key, value, {
      dateKeys: { gte: 'start_date', lte: 'end_date' }
    });
    setFilter(prev => ({ ...prev, ...updatedValues }));
    onFilter?.(formattedValues);
  }

  const isEnabledField = (key) => filterFields.includes(key);

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleOpenExportModal = () => setExportModalProps({...exportModalProps, open: true});

  const handleOkExportModal = (data) => {
    const query = tableHelpers.formatFilterQuery({
      filter,
      data,
      exportAdditionalFields
    });

    if (!query) return;

    setExportModalProps({...exportModalProps, loading: true});

    dispatch(budgetActions.exportBudgetRequests({
      query,
      successCallback: (response) => {
        fileHelpers.handleCSVExport(response, tableHelpers.getExportFileName({baseName: 'budgets', data}));
        handleCancelExportModal();
      },
      errorCallback: (response) => {
        setExportModalProps({
          ...exportModalProps,
          error: response?.response?.data?.message || t('validation.exportError'),
          loading: false
        });
      }
    }));
  }

  const handleOpenAgentChat = () => {
    dispatch(agentActions.openAgentChat({variant: agentTypeConstants.ADD_NEW_BUDGET}));
  }

  return (
    <StyledTableFilters {...rest}>
      <StyledTableFiltersElements>
        <StyledTableFiltersElementsSpace
          size='small'
        >
          {isEnabledField('search') && (
            <StyledTableFiltersSearch
              allowClear
              onSearch={(value) => handleOnFilter('search', value)}
              placeholder={t('search')}
              value={filter.search}
              size='large'
            />
          )}
          {isEnabledField('status') && (
            <Select
              allowClear
              onChange={(value) => handleOnFilter('status', value)}
              options={formHelpers.getStatusOptions(statusOptions)}
              placeholder={t('allStatuses')}
              size='large'
              value={filter.status}
            />
          )}
          {isEnabledField('date_range') && (
            <DatePicker.RangePicker
              size='large'
              disabledDate={(current) => current.isAfter(dayjs())}
              onChange={(value) => handleOnFilter('date_range', value)}
              panelRender={dateHelpers.getDatePickerPanelRender}
              format='DD MMM YYYY'
              superNextIcon={false}
              superPrevIcon={false}
            />
          )}
          {isEnabledField('owner') && (
            <StyledTableFiltersSelect
              allowClear
              showSearch
              size='large'
              styles={{ popup: { root: {minWidth: 235} } }}
              onChange={(value) => handleOnFilter('reported_by', value)}
              optionFilterProp='optionLabel'
              optionLabelProp='optionLabel'
              options={employeeOptions}
              placeholder={t('Owner')}
            />
          )}
          {isEnabledField('approver') && (
            <StyledTableFiltersSelect
              allowClear
              showSearch
              size='large'
              styles={{ popup: { root: {minWidth: 235} } }}
              onChange={(value) => handleOnFilter('active_approver', value)}
              optionFilterProp='optionLabel'
              optionLabelProp='optionLabel'
              options={employeeOptions}
              placeholder={t('approver')}
            />
          )}
        </StyledTableFiltersElementsSpace>
      </StyledTableFiltersElements>

      {enabledActionsSection && (
        <Space size='small'>
          <Button
            onClick={handleOpenExportModal}
            size='large'
          >
            {t('export')}
          </Button>
          <Button
            icon={<StarsIcon />}
            onClick={handleOpenAgentChat}
            size='large'
            type='primary'
          >
            + {t('request')}
          </Button>
        </Space>
      )}

      <ExportModal
        {...exportModalProps}
        options={['csv']}
        enableFormatField={false}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExportModal}
        title={t('export')}
      />
    </StyledTableFilters>
  );
}

TableFilters.propTypes = {
  enabledActionsSection: PropTypes.bool,
  defaultQuery: PropTypes.object,
  filterFields: PropTypes.arrayOf(PropTypes.string),
  tableType: PropTypes.oneOf(['active', 'requests']),
  onFilter: PropTypes.func,
}

export default TableFilters;
